import { api, handleApiError } from '../client'

export interface Integration {
  id?: string
  type: string
  name: string
  description: string
  icon: string
  category: 'storage' | 'communication' | 'analytics' | 'ai' | 'billing' | 'automation'
  isConnected: boolean
  isEnabled: boolean
  status: string
  lastTestedAt?: string
  lastError?: string
  createdAt?: string
  updatedAt?: string
}

export interface ConnectIntegrationRequest {
  apiKey?: string
  baseUrl?: string
  defaultModel?: string
  additionalConfig?: Record<string, string>
}

export interface ToggleIntegrationRequest {
  enabled: boolean
}

export interface IntegrationTestResult {
  isSuccess: boolean
  status: string
  errorMessage?: string
  testedAt: string
  details?: Record<string, unknown>
}

export interface AIModelInfo {
  id: string
  name: string
  description?: string
  provider?: string
  contextLength?: number
  inputCostPer1M?: number
  outputCostPer1M?: number
  isRecommended: boolean
}

export const integrationService = {
  async getIntegrations(tenantId?: string): Promise<Integration[]> {
    try {
      const params = tenantId ? { tenantId } : undefined
      const response = await api.get<Integration[]>('/v1/integrations', { params })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getIntegration(type: string): Promise<Integration> {
    try {
      const response = await api.get<Integration>(`/v1/integrations/${type}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async connectIntegration(type: string, config: ConnectIntegrationRequest): Promise<Integration> {
    try {
      const response = await api.post<Integration>(`/v1/integrations/${type}/connect`, config)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async disconnectIntegration(type: string): Promise<void> {
    try {
      await api.delete(`/v1/integrations/${type}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async toggleIntegration(type: string, enabled: boolean): Promise<Integration> {
    try {
      const response = await api.put<Integration>(`/v1/integrations/${type}/toggle`, { enabled })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async testIntegration(type: string): Promise<IntegrationTestResult> {
    try {
      const response = await api.post<IntegrationTestResult>(`/v1/integrations/${type}/test`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async testCredentials(type: string, config: ConnectIntegrationRequest): Promise<IntegrationTestResult> {
    try {
      const response = await api.post<IntegrationTestResult>(`/v1/integrations/${type}/test-credentials`, config)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getModels(type: string, config: ConnectIntegrationRequest): Promise<AIModelInfo[]> {
    try {
      const response = await api.post<AIModelInfo[]>(`/v1/integrations/${type}/models`, config)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
